import React, {useEffect, useMemo, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {AspectRatio, Box, Button, Container, Flex, HStack, Icon, Image, Select, SimpleGrid, Text, Wrap, WrapItem} from "@chakra-ui/react";
import {catalogPhotos, categories, imageUrl, subCategoryUrl} from "../data/catalog";
import {galleryVideos} from "../data/gallery";
import PageBanner from "../components/PageBanner";
import Lightbox from "../components/Lightbox";
import usePageTitle from "../usePageTitle";
import bannerImage from "../gallery/cover_2.jpg";

const PAGE_SIZE = 24;

const categoryTitle = (path) => categories.find((c) => c.path === path)?.title;

// "All" view: one photo from each category in turn, so the grid doesn't open with 15 pivot doors.
// Photos listed under two categories are shown once.
const mixedPhotos = (() => {
    const queues = categories.map((c) => catalogPhotos.filter((p) => p.category === c));
    const seen = new Set();
    const result = [];
    while (queues.some((q) => q.length)) {
        queues.forEach((q) => {
            const photo = q.shift();
            if (photo && !seen.has(photo.file)) {
                seen.add(photo.file);
                result.push(photo);
            }
        });
    }
    return result;
})();

function PlayIcon(props) {
    return (
        <Icon viewBox="0 0 24 24" {...props}>
            <path fill="currentColor" d="M8 5v14l11-7z"/>
        </Icon>
    );
}

function Tile({item, onClick}) {
    const isVideo = item.type === "video";
    return (
        <AspectRatio ratio={1}>
            <Box as="button" type="button" data-group overflow="hidden" position="relative" bg="ink.900" onClick={onClick}
                 aria-label={`Open ${isVideo ? "video" : "photo"}: ${item.title}`}>
                {(item.poster || !isVideo) && (
                    <Image src={isVideo ? item.poster : item.src} alt={item.title} loading="lazy" w="100%" h="100%"
                           objectFit="cover" transition="transform 0.6s ease" _groupHover={{transform: "scale(1.06)"}}/>
                )}
                {isVideo && (
                    <Flex position="absolute" inset={0} align="center" justify="center">
                        <Flex w="64px" h="64px" borderRadius="full" bg="brand.500" color="white" align="center" justify="center"
                              boxShadow="lg" transition="transform 0.2s" _groupHover={{transform: "scale(1.1)"}}>
                            <PlayIcon boxSize={7} ml={1}/>
                        </Flex>
                    </Flex>
                )}
                <Box position="absolute" left={0} right={0} bottom={0} p={4} textAlign="left"
                     bgImage="linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0))"
                     opacity={0} _groupHover={{opacity: 1}} transition="opacity 0.3s">
                    <Text color="white" fontSize="sm" fontWeight={600} noOfLines={2}>{item.title}</Text>
                    {item.subtitle && <Text color="whiteAlpha.700" fontSize="xs" noOfLines={1}>{item.subtitle}</Text>}
                </Box>
            </Box>
        </AspectRatio>
    );
}

function FilterButton({active, children, ...rest}) {
    return (
        <Button size="sm" variant={active ? "solid" : "outline"} colorScheme={active ? "brand" : "gray"}
                borderColor={active ? "brand.500" : "ink.200"} color={active ? "white" : "ink.700"}
                fontWeight={600} letterSpacing="0.04em" {...rest}>
            {children}
        </Button>
    );
}

function GalleryPage() {
    usePageTitle("Gallery");
    const [params, setParams] = useSearchParams();
    const [visible, setVisible] = useState(PAGE_SIZE);
    const [open, setOpen] = useState(null);

    const filter = params.get("category") || "all";
    const hasVideos = galleryVideos.length > 0;
    const tab = hasVideos && params.get("type") === "videos" ? "videos" : "photos";

    useEffect(() => setVisible(PAGE_SIZE), [filter, tab]);

    const photoItems = useMemo(() => {
        const photos = filter === "all" ? mixedPhotos : catalogPhotos.filter((p) => p.category.path === filter);
        return photos.map(({file, category, sub}) => ({
            type: "image",
            src: imageUrl(file),
            title: sub.description,
            subtitle: category.title,
            href: subCategoryUrl(category, sub),
        }));
    }, [filter]);

    const videoItems = useMemo(() => galleryVideos
        .filter((v) => filter === "all" || v.category === filter)
        .map((v) => ({type: "video", ...v, subtitle: categoryTitle(v.category)})), [filter]);

    const items = tab === "videos" ? videoItems : photoItems;
    const shown = items.slice(0, visible);

    const setParam = (key, value) => setParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value); else next.delete(key);
        return next;
    }, {replace: true});

    const countFor = (path) => tab === "videos"
        ? galleryVideos.filter((v) => v.category === path).length
        : catalogPhotos.filter((p) => p.category.path === path).length;

    const filters = [
        {value: "all", label: "All", count: tab === "videos" ? galleryVideos.length : mixedPhotos.length},
        ...categories.map((c) => ({value: c.path, label: c.title, count: countFor(c.path)})).filter((f) => f.count),
    ];

    return (
        <>
            <PageBanner image={bannerImage} title="Gallery" crumbs={[{label: "Home", to: "/"}, {label: "Gallery"}]}>
                <Text>A selection of doors, windows, stair cases and joinery we have custom made and installed.</Text>
            </PageBanner>

            <Container maxW="container.xl" py={{base: 10, md: 16}}>
                {hasVideos && (
                    <HStack spacing={0} mb={6} borderBottom="1px solid" borderColor="ink.100">
                        {[["photos", "Photos"], ["videos", "Videos"]].map(([value, label]) => (
                            <Box key={value} as="button" type="button" px={5} py={3} fontWeight={700} fontSize="sm"
                                 letterSpacing="0.1em" textTransform="uppercase" mb="-1px" borderBottom="3px solid"
                                 borderColor={tab === value ? "brand.500" : "transparent"}
                                 color={tab === value ? "ink.900" : "ink.500"}
                                 onClick={() => setParam("type", value === "videos" ? "videos" : null)}>
                                {label}
                            </Box>
                        ))}
                    </HStack>
                )}

                {/* Phones get a dropdown; the full row of category buttons is too wide */}
                <Select display={{base: "block", md: "none"}} mb={6} value={filter} aria-label="Filter by category"
                        onChange={(e) => setParam("category", e.target.value === "all" ? null : e.target.value)}>
                    {filters.map((f) => <option key={f.value} value={f.value}>{f.label} ({f.count})</option>)}
                </Select>
                <Wrap spacing={2} mb={8} display={{base: "none", md: "flex"}}>
                    {filters.map((f) => (
                        <WrapItem key={f.value}>
                            <FilterButton active={filter === f.value}
                                          onClick={() => setParam("category", f.value === "all" ? null : f.value)}>
                                {f.label} ({f.count})
                            </FilterButton>
                        </WrapItem>
                    ))}
                </Wrap>

                {shown.length ? (
                    <SimpleGrid columns={{base: 2, md: 3, lg: 4}} spacing={{base: 2, md: 4}}>
                        {shown.map((item, i) => (
                            <Tile key={`${item.src || item.embed}-${i}`} item={item} onClick={() => setOpen(i)}/>
                        ))}
                    </SimpleGrid>
                ) : (
                    <Text color="ink.500" py={16} textAlign="center">Nothing here yet.</Text>
                )}

                {visible < items.length && (
                    <Flex justify="center" mt={10}>
                        <Button variant="outline" colorScheme="gray" borderColor="ink.800" color="ink.900" size="lg"
                                _hover={{bg: "ink.900", color: "white"}} onClick={() => setVisible((v) => v + PAGE_SIZE)}>
                            Load more ({items.length - visible})
                        </Button>
                    </Flex>
                )}
            </Container>

            <Lightbox items={items} index={open} onClose={() => setOpen(null)} onChange={setOpen}/>
        </>
    );
}

export default GalleryPage;
